import Messages from './messages.js'

/**
 * Represents a playlist item
 * 
 * @typedef {Object} PlaylistTrack
 * @property {string} id - Track id
 * @property {string} title - Track title
 * @property {string} url - URL of the video - file:// or https://
 * @property {number} duration - Total duration of the video in seconds
 * @property {number} played - Played time in seconds
 */

/**
 * Represents a play status
 * 
 * @typedef {Object} PlayStatus
 * @property {string} videoId - Video id
 * @property {string} status - Current play status
 * @property {Array<PlaylistTrack>} tracks - Track wise status
 * @property {object|undefined} error - Error detail
 */

export default class VideoPlayerClient {

  constructor(socket) {
    this.socket = socket
  }

  /**
   * Start play
   * 
   * @param {Object} params
   * @param {string} params.videoId - Video id
   * @param {Array<PlaylistTrack>} params.tracks - List of tracks
   * @param {number} params.startTrack - Track to start from
   * @param {number} params.startTime - Time to start from in seconds
   * @param {string} params.subtitleLang - Subtitle language
   * @param {function(PlayStatus):void} params.onStatus - Callback for status updates
   * @returns {Promise<void>}
   */
  async start({ videoId, tracks, startTrack, startTime, subtitleLang, onStatus }) {
    //start play
    try {
      await this.socket.emitWithAck(Messages.VideoPlayer_Start, videoId, tracks, startTrack, startTime, subtitleLang);
      //listen to status
      this.onStatus(videoId, onStatus)
    } catch (error) {
      console.error(`VideoPlayerClient : ${error}`)
      throw new Error('Socket Error')
    }
  }

  /**
   * On status
   * 
   * @param {string} videoId 
   * @param {function(PlayStatus):} onStatus
   * @returns {Promise<void>}
   */
  onStatus(videoId, onStatus) {
    this.socket.on(Messages.VideoPlayer_Status, (statusUpdate) => {
      if (statusUpdate.videoId == videoId) {
        onStatus(statusUpdate)
      }
    })
  }

  /**
   * Status request
   * 
   * @param {string} videoId 
   * @returns {Promise<void>}
   */
  async statusRequest(videoId) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.VideoPlayer_StatusRequest, videoId);
    } catch (error) {
      console.error(`VideoPlayerClient : statusRequest : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
  }

  /**
   * Pause current play
   * 
   * @param {string} videoId 
   * @returns {Promise<void>}
   */
  async pause(videoId) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.VideoPlayer_Pause, videoId);
    } catch (error) {
      console.error(`VideoPlayerClient : pause : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
  }

  /**
   * Resume current play
   * 
   * @param {string} videoId 
   * @returns {Promise<void>}
   */
  async resume(videoId) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.VideoPlayer_Resume, videoId);
    } catch (error) {
      console.error(`VideoPlayerClient : resume : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
  }

  /**
   * Stop current play
   * 
   * @param {string} videoId 
   * @returns {Promise<void>}
   */
  async stop(videoId) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.VideoPlayer_Stop, videoId);
    } catch (error) {
      console.error(`VideoPlayerClient : stop : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
  }

  /**
   * Change subtitle language for currently playing video
   * 
   * @param {string} videoId 
   * @param {string} lang
   * @returns {Promise<void>}
   */
  async subtitleLang(videoId, lang) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.VideoPlayer_ChangeSubtitle, videoId, lang);
    } catch (error) {
      console.error(`VideoPlayerClient : subtitleLang : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
  }
}